import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const sections = [
    {
      title: "Introduction",
      content: (
        <div className="space-y-4 text-gray-600">
          <p>
            Welcome to Daily Dog. We understand that you care about how your
            information is used and shared, and we take your privacy seriously.
            This Privacy Policy outlines our practices concerning the handling
            of information when you use our mobile application. We have designed
            Daily Dog with privacy in mind, focusing on providing valuable dog
            training content while minimizing data collection and storage.
          </p>
          <p>
            By using Daily Dog, you agree to the practices described in this
            Privacy Policy. We encourage you to read this document carefully to
            understand our approach to handling information in our application.
          </p>
        </div>
      ),
    },
    {
      title: "Overview of Our Privacy-First Approach",
      content: (
        <p className="text-gray-600">
          Daily Dog operates on a privacy-first principle, where we deliberately
          minimize data collection and storage. Our application is designed to
          function primarily through local device storage, ensuring that your
          usage of the app remains private and under your control. The only
          server interaction occurs when downloading exercise guides and
          displaying occasional advertisements.
        </p>
      ),
    },
    {
      title: "Information Collection and Use",
      content: (
        <div className="space-y-6">
          <p className="text-gray-600">
            Daily Dog is designed to provide valuable dog training content while
            maintaining user privacy. We have intentionally structured our
            application to minimize data collection and rely primarily on local
            device storage rather than cloud-based solutions.
          </p>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Local Device Storage
            </h3>
            <p className="text-gray-600 mb-4">
              The following information is stored locally on your device:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>
                Dog exercise guides and training content downloaded from our API
                for offline access
              </li>
              <li>
                Your preferred language setting (English or German) to provide
                content in your chosen language
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      title: "Advertising and Third-Party Services",
      content: (
        <div className="space-y-4 text-gray-600">
          <p>
            Daily Dog incorporates Google AdMob for displaying video
            advertisements when users choose to unlock additional exercise
            guides. We have specifically chosen to implement advertising in a
            way that minimizes data collection while still allowing us to
            provide valuable content.
          </p>
          <p>When you interact with an advertisement:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Google AdMob may collect and process certain data according to
              their privacy policy
            </li>
            <li>
              This data collection is governed by Google's privacy practices and
              terms of service
            </li>
            <li>
              We do not receive or store any personal information from these
              advertising interactions
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Data Security",
      content: (
        <div className="space-y-4 text-gray-600">
          <p>
            We take the security of our application and its content delivery
            seriously. Our security measures include:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Secure HTTPS connections for all API communications</li>
            <li>
              Regular security updates and monitoring of our server
              infrastructure
            </li>
            <li>
              Utilization of modern encryption standards for data transmission
            </li>
            <li>
              Reliance on your device's built-in security features for local
              data protection
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Contact Us",
      content: (
        <div className="space-y-4 text-gray-600">
          <p>
            If you have any questions, concerns, or feedback about this Privacy
            Policy or our privacy practices, please don't hesitate to contact us
            at:
          </p>
          <p className="font-medium">privacy@dailydog.app</p>
          <p>
            We welcome your questions and comments about privacy and are
            committed to addressing any concerns you may have about our privacy
            practices.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="logo-container" style={{ filter: "brightness(0.7)" }}>
        <Link to="/">
          <img src="/logo.png" alt="Daily Dog Logo" className="logo" />
        </Link>
        <Link to="/" className="site-title" style={{ textDecoration: "none" }}>
          Daily Dog
        </Link>
      </div>

      <div className="privacy-policy space-y-8">
        <header className="border-b pb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy for Daily Dog
          </h1>
          <p className="text-gray-600">Last updated: January 27, 2025</p>
        </header>

        <main className="space-y-12">
          {sections.map((section, index) => (
            <section key={index}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {section.title}
              </h2>
              {section.content}
            </section>
          ))}
        </main>
      </div>
      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-logo">
            <img
              src="/logo.png"
              alt="Daily Dog Logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/40x40?text=DD";
              }}
            />
            <h3 style={{ fontFamily: "Baloo, sans-serif" }}>Daily Dog</h3>
          </div>
          <div className="footer-links">
            <div className="footer-links-column">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              </ul>
            </div>
            <div className="footer-links-column">
              <h4>Help</h4>
              <ul>
                <li><Link to="/support">Support</Link></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Daily Dog. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
};

export default PrivacyPolicy;
